import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Eye, EyeOff, CheckCircle2, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

export function validatedOAuthReturn(search: string, origin: string): string | null {
  const candidate = new URLSearchParams(search).get("oauth_return");
  if (!candidate || !candidate.startsWith("/oauth/authorize?")) return null;
  try {
    const target = new URL(candidate, origin);
    if (
      target.origin !== origin ||
      target.pathname !== "/oauth/authorize" ||
      target.username ||
      target.password ||
      target.hash
    ) return null;
    return `${target.pathname}${target.search}`;
  } catch {
    return null;
  }
}

export default function LandingPage() {
  const { toast } = useToast();
  const [mode, setMode] = useState<"signin" | "signup" | "reset">("signin");
  const [resetStep, setResetStep] = useState<"email" | "code" | "done">("email");
  const [resetCode, setResetCode] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPending(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.status === 403 && data.message?.includes("pending approval")) {
        setPendingApproval(true);
        return;
      }
      if (!res.ok) {
        throw new Error(data.message || "Login failed");
      }
      // OAuth browser handoffs are accepted only for the exact same-origin
      // authorization route. The server validates the full request again
      // before creating the user-bound, one-time consent transaction.
      let dest = validatedOAuthReturn(window.location.search, window.location.origin) ?? "/";
      try {
        const saved = sessionStorage.getItem("postLoginRedirect");
        if (dest === "/" && saved && saved.startsWith("/")) dest = saved;
        sessionStorage.removeItem("postLoginRedirect");
      } catch {
        // sessionStorage unavailable — fall back to home.
      }
      window.location.href = dest;
    } catch (err: any) {
      toast({ title: "Login failed", description: err.message, variant: "destructive" });
    } finally {
      setIsPending(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPending(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, firstName, lastName }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Signup failed");
      }
      setSignupSuccess(true);
    } catch (err: any) {
      toast({ title: "Signup failed", description: err.message, variant: "destructive" });
    } finally {
      setIsPending(false);
    }
  };

  const requestResetCode = async () => {
    setIsPending(true);
    setResetError("");
    try {
      const res = await fetch("/api/auth/password-reset/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not request a reset code");
      setResetStep("code");
      setResendSeconds(data.cooldownSeconds || 60);
    } catch (err: any) {
      setResetError(err.message);
    } finally {
      setIsPending(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetStep === "email") return requestResetCode();
    setResetError("");
    if (password !== confirmPassword) {
      setResetError("Passwords do not match.");
      return;
    }
    setIsPending(true);
    try {
      const res = await fetch("/api/auth/password-reset/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: resetCode, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not reset password");
      setResetStep("done");
    } catch (err: any) {
      setResetError(err.message);
    } finally {
      setIsPending(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setShowPassword(false);
    setSignupSuccess(false);
    setPendingApproval(false);
    setResetStep("email");
    setResetCode("");
    setConfirmPassword("");
    setResetError("");
    setResendSeconds(0);
  };

  if (pendingApproval) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/6 rounded-full blur-[120px]" />

        <div className="relative w-full max-w-[400px] space-y-8">
          <div className="text-center space-y-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500 mx-auto">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tighter" data-testid="text-pending-approval">Pending Approval</h1>
              <p className="text-sm text-muted-foreground mt-1">Your account is awaiting admin review</p>
            </div>
          </div>

          <div className="rounded-2xl border border-border/50 bg-card p-6 sm:p-8 space-y-4">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                An administrator will review and approve your account. You'll be able to sign in once your access has been granted.
              </p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setMode("signin");
              }}
              variant="outline"
              className="w-full rounded-full h-11 text-sm font-medium"
              data-testid="button-pending-back-to-signin"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/6 rounded-full blur-[120px]" />

        <div className="relative w-full max-w-[400px] space-y-8">
          <div className="text-center space-y-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500 mx-auto">
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tighter" data-testid="text-signup-success">Account Created</h1>
              <p className="text-sm text-muted-foreground mt-1">Your account is pending admin approval</p>
            </div>
          </div>

          <div className="rounded-2xl border border-border/50 bg-card p-6 sm:p-8 space-y-4">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                An administrator will review your request and grant access shortly. You'll be able to sign in once approved.
              </p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setMode("signin");
              }}
              variant="outline"
              className="w-full rounded-full h-11 text-sm font-medium"
              data-testid="button-back-to-signin"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/6 rounded-full blur-[120px]" />

      <div className="relative w-full max-w-[400px] space-y-8">
        <div className="text-center space-y-3">
          <div>
            <h1
              className="inline-flex items-center justify-center gap-2 font-sans text-[27px] sm:text-[31px] font-bold sm:font-medium leading-none tracking-[-1.2px] sm:tracking-[-1.45px]"
              data-testid="text-logo"
            >
              <svg
                aria-hidden="true"
                className="h-7 w-7 shrink-0 text-violet-600"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  d="M16 1.5C16.8 9.8 22.2 15.2 30.5 16C22.2 16.8 16.8 22.2 16 30.5C15.2 22.2 9.8 16.8 1.5 16C9.8 15.2 15.2 9.8 16 1.5Z"
                  fill="currentColor"
                />
              </svg>
              <span>personaos</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === "signin" ? "Sign in to your workspace" : mode === "signup" ? "Create your account" : "Reset your password"}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-border/50 bg-card p-6 sm:p-8 space-y-5">
          {mode !== "reset" && <div className="flex rounded-full bg-muted p-1 border border-border/40" data-testid="auth-mode-toggle">
            <button
              type="button"
              className={`flex-1 text-sm font-medium py-2 rounded-full transition-all ${
                mode === "signin"
                  ? "bg-background shadow-sm text-foreground border border-border/30"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => { setMode("signin"); resetForm(); }}
              data-testid="button-switch-signin"
            >
              Sign In
            </button>
            <button
              type="button"
              className={`flex-1 text-sm font-medium py-2 rounded-full transition-all ${
                mode === "signup"
                  ? "bg-background shadow-sm text-foreground border border-border/30"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => { setMode("signup"); resetForm(); }}
              data-testid="button-switch-signup"
            >
              Sign Up
            </button>
          </div>}

          {mode === "reset" ? (
            resetStep === "done" ? (
              <div className="space-y-4 text-center" aria-live="polite">
                <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
                <div>
                  <h2 className="font-semibold" data-testid="text-reset-success">Password reset</h2>
                  <p className="text-sm text-muted-foreground mt-1">You can now sign in with your new password.</p>
                </div>
                <Button
                  type="button"
                  className="w-full rounded-full h-11"
                  onClick={() => { resetForm(); setMode("signin"); }}
                  data-testid="button-reset-back-to-signin"
                >
                  Return to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4" data-testid="form-password-reset">
                <p className="text-sm text-muted-foreground">
                  {resetStep === "email"
                    ? "Enter your account email and we’ll send a six-digit confirmation code."
                    : `Enter the code sent to ${email} and choose a new password.`}
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    disabled={resetStep === "code"} autoFocus data-testid="input-reset-email" />
                </div>
                {resetStep === "code" && <>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-code">Confirmation code</Label>
                    <Input id="reset-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                      value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
                      data-testid="input-reset-code" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-password">New password</Label>
                    <Input id="reset-password" type="password" minLength={8} value={password}
                      onChange={(e) => setPassword(e.target.value)} data-testid="input-reset-password" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-confirm-password">Confirm new password</Label>
                    <Input id="reset-confirm-password" type="password" minLength={8} value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)} data-testid="input-reset-confirm-password" />
                  </div>
                </>}
                {resetError && <p role="alert" className="text-sm text-destructive">{resetError}</p>}
                <Button type="submit" disabled={isPending || !email || (resetStep === "code" && (resetCode.length !== 6 || password.length < 8 || !confirmPassword))}
                  className="w-full rounded-full h-11" data-testid="button-reset-submit">
                  {isPending ? "Please wait..." : resetStep === "email" ? "Send Reset Code" : "Set New Password"}
                </Button>
                {resetStep === "code" && (
                  <button type="button" className="w-full text-sm text-primary disabled:text-muted-foreground"
                    disabled={isPending || resendSeconds > 0}
                    onClick={requestResetCode} data-testid="button-reset-resend">
                    {resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : "Resend code"}
                  </button>
                )}
                <button type="button" className="w-full text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => { resetForm(); setMode("signin"); }} data-testid="button-reset-cancel">
                  Back to Sign In
                </button>
              </form>
            )
          ) : mode === "signin" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-email" className="text-sm font-medium">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                    autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11"
                  autoFocus
                  data-testid="input-login-email"
                />
              </div>
              <button type="button" className="text-sm text-primary hover:underline"
                onClick={() => { setPassword(""); setMode("reset"); }} data-testid="button-forgot-password">
                Forgot password?
              </button>
              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="h-11 pr-10"
                    data-testid="input-login-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isPending || !email || !password}
                className="w-full rounded-full h-11 text-sm font-medium"
                data-testid="button-login-submit"
              >
                {isPending ? "Signing in..." : "Sign In"}
                {!isPending && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="signup-first-name" className="text-sm font-medium">First Name</Label>
                  <Input
                    id="signup-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    className="h-11"
                    autoFocus
                    data-testid="input-signup-first-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signup-last-name" className="text-sm font-medium">Last Name</Label>
                  <Input
                    id="signup-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="h-11"
                    data-testid="input-signup-last-name"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-email" className="text-sm font-medium">Email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11"
                  data-testid="input-signup-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="h-11 pr-10"
                    data-testid="input-signup-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isPending || !email || password.length < 6}
                className="w-full rounded-full h-11 text-sm font-medium"
                data-testid="button-signup-submit"
              >
                {isPending ? "Creating account..." : "Create Account"}
                {!isPending && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          The creative operating system for DTC brands.
        </p>
      </div>
    </div>
  );
}
